/**
 * Плавающая панель над выделенными референсами — как `SelectionBar` трендов:
 * внизу рабочей области, по центру относительно `main`.
 *
 * Действия этой панели: теги (одно меню «Теги»: повесить, у висящего — «×» снять),
 * вердикт, перенос и копия в другую библиотеку, корзина, id в буфер, CSV, удаление,
 * «Генерировать» (ссылка на `/generate` с выделенными референсами). Кэша панель не знает:
 * мутации и итог даёт владелец (`ReferenceBrowser`).
 * При `foldersOnly` (выделена обложка папки модели) остаются теги, вердикт, генерация по
 * обложкам, удаление, «все видимые» и «снять». Удаление папки — вся модель: какие id уходят,
 * решает владелец, панели — только числа для подтверждения (`toDelete`). Остальное по папке
 * не имеет смысла — действие коснулось бы одной обложки, а не всей папки.
 */
import {
  ChevronDown,
  Copy,
  Download,
  FolderInput,
  Gauge,
  Shirt,
  ShoppingBasket,
  Tag as TagIcon,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router";

import { LibraryPicker } from "@/components/library/LibraryPicker";
import { TagPicker, usePopoverClose } from "@/components/trends/TagPicker";
import { btn, btnGhost, ErrorLine, Segmented } from "@/components/trends/ui";
import { plural, verdictLabel, type Verdict } from "@/lib/library";
import { num } from "@/lib/media";
import type { Tag } from "@/lib/tags";

type Open = "tags" | "verdict" | "lib" | null;

export function ReferenceSelectionBar({
  count,
  visibleCount,
  onSelectVisible,
  onClear,
  basketMode,
  onBasket,
  idsText,
  onCopyIds,
  onCsv,
  onDelete,
  toDelete,
  tags,
  tagCounts,
  onTag,
  onCreateTag,
  onUntag,
  onVerdict,
  onMove,
  onCopy,
  lastLibraryId,
  busy,
  error,
  result,
  foldersOnly = false,
  generate,
}: {
  /** На уровне папок выделено не только видимая россыпь (обложки папок): панель урезана до тегов, вердикта, генерации и удаления. */
  foldersOnly?: boolean;
  /** Адрес «Генерации» с выделенными референсами; больше `max` — кнопка неактивна. */
  generate: { to: string; max: number };
  count: number;
  visibleCount: number;
  onSelectVisible: () => void;
  onClear: () => void;
  basketMode: "add" | "remove";
  onBasket: () => Promise<void>;
  /** Строка id выделенных: показывается для ручного копирования, если буфера нет. */
  idsText: string;
  /** `false` — буфер недоступен. */
  onCopyIds: () => Promise<boolean>;
  onCsv: () => void;
  onDelete: () => Promise<void>;
  /**
   * Что уйдёт при удалении: выделенные папки, все фото (у папки — вся модель) и сумма их
   * генераций — после удаления они остаются в карточках платьев.
   */
  toDelete: { folders: number; photos: number; generations: number };
  tags: Tag[];
  /** Тег → на скольких выделенных он уже висит. */
  tagCounts: ReadonlyMap<number, number>;
  onTag: (tagId: number) => Promise<void>;
  onCreateTag: (name: string) => Promise<void>;
  onUntag: (tagId: number) => Promise<void>;
  /** `null` снимает вердикт. */
  onVerdict: (verdict: Verdict | null) => Promise<void>;
  onMove: (libraryId: number) => Promise<void>;
  onCopy: (libraryId: number) => Promise<void>;
  lastLibraryId: number | null;
  busy: boolean;
  error: unknown;
  result: string | null;
}) {
  // Строка, для которой буфер отказал; сменилось выделение — подсказка уходит.
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const close = useCallback(() => setOpen(null), []);
  const toggle = (what: Exclude<Open, null>) => setOpen((v) => (v === what ? null : what));
  // Клик по кнопке открытого поповера не должен сначала закрыть его «кликом мимо».
  const keep = (e: MouseEvent) => e.stopPropagation();
  const run = (job: Promise<void>) => job.then(close, () => undefined);

  const copy = async () => {
    setFailedFor((await onCopyIds()) ? null : idsText);
  };
  const askDelete = () => {
    const { folders, photos, generations } = toDelete;
    const what =
      folders > 0
        ? `${num(folders)} ${plural(folders, "папку", "папки", "папок")} (${num(photos)} фото)`
        : `${num(photos)} ${plural(photos, "референс", "референса", "референсов")}`;
    const gens =
      generations > 0
        ? ` ${num(generations)} ${plural(generations, "генерация останется", "генерации останутся", "генераций останутся")} в карточках платьев.`
        : "";
    if (window.confirm(`Удалить ${what} из библиотек? Они уйдут из корзин и проектов.${gens}`)) {
      void onDelete().catch(() => undefined);
    }
  };

  return (
    <div className="pointer-events-none fixed right-0 bottom-4 left-[var(--spacing-sidebar)] z-40 flex justify-center px-4">
      <div
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-border bg-bg-elevated px-3 py-2 text-[12.5px] shadow-xl"
        data-annot="панель выделения референсов"
      >
        <span className="font-medium text-fg tnum">Выделено {num(count)}</span>
        <button
          type="button"
          className={btnGhost}
          onClick={onSelectVisible}
          disabled={visibleCount === 0}
          title="Добавить к выделению все референсы под текущим фильтром"
        >
          все видимые ({num(visibleCount)})
        </button>
        <button type="button" className={btnGhost} onClick={onClear} title="Снять выделение (Esc)">
          <X size={12} />
          снять
        </button>
        {count > generate.max ? (
          <span className={`${btn} cursor-not-allowed opacity-50`} aria-disabled="true" title={`не больше ${generate.max} за раз`}>
            <Shirt size={13} />
            Генерировать {num(count)}
          </span>
        ) : (
          <Link className={btn} to={generate.to} title="Выделенные (у папки — обложка) — референсами на страницу «Генерация»">
            <Shirt size={13} />
            Генерировать {num(count)}
          </Link>
        )}

        <span className="h-5 w-px bg-border" />

        <div className="relative">
          <button
            type="button"
            className={btn}
            aria-expanded={open === "tags"}
            onMouseDown={keep}
            onClick={() => toggle("tags")}
            disabled={busy}
          >
            <TagIcon size={13} />
            Теги
            <ChevronDown size={12} />
          </button>
          {open === "tags" ? (
            <TagPicker
              tags={tags}
              counts={tagCounts}
              total={count}
              mode="both"
              busy={busy}
              onPick={(id) => void run(onTag(id))}
              onUnpick={(id) => void run(onUntag(id))}
              onCreate={(name) => void run(onCreateTag(name))}
              onClose={close}
            />
          ) : null}
        </div>
        <div className="relative">
          <button
            type="button"
            className={btn}
            aria-expanded={open === "verdict"}
            onMouseDown={keep}
            onClick={() => toggle("verdict")}
            disabled={busy}
          >
            <Gauge size={13} />
            Вердикт
            <ChevronDown size={12} />
          </button>
          {open === "verdict" ? (
            <VerdictCard onVerdict={(v) => void run(onVerdict(v))} onClose={close} busy={busy} />
          ) : null}
        </div>
        {foldersOnly ? (
          <>
            <span className="text-[11.5px] text-fg-faint">с папками — теги, вердикт, генерация и удаление</span>
            <DeleteButton busy={busy} onClick={askDelete} />
          </>
        ) : null}
        {foldersOnly ? null : (
          <>
            <div className="relative">
              <button
                type="button"
                className={btn}
                aria-expanded={open === "lib"}
                onMouseDown={keep}
                onClick={() => toggle("lib")}
                disabled={busy}
              >
                <FolderInput size={13} />В библиотеку
                <ChevronDown size={12} />
              </button>
              {open === "lib" ? (
                <LibraryCard
                  count={count}
                  lastLibraryId={lastLibraryId}
                  onMove={onMove}
                  onCopy={onCopy}
                  onClose={close}
                  busy={busy}
                />
              ) : null}
            </div>

            <span className="h-5 w-px bg-border" />

            <button type="button" className={btn} disabled={busy} onClick={() => void onBasket().catch(() => undefined)}>
              <ShoppingBasket size={13} />
              {basketMode === "add" ? "В корзину" : "Убрать из корзины"}
            </button>
            <button type="button" className={btn} onClick={() => void copy()} title="Список id — вставить в чат или CLI">
              <Copy size={13} />
              Копировать id
            </button>
            <button type="button" className={btn} onClick={onCsv} title="Таблица для Excel: id, библиотека, теги, ссылка">
              <Download size={13} />
              Скачать CSV
            </button>
            <DeleteButton busy={busy} onClick={askDelete} />
          </>
        )}

        {result ? <div className="basis-full text-[12px] text-fg-muted">{result}</div> : null}
        {failedFor !== null && failedFor === idsText ? (
          <div className="grid basis-full gap-1">
            <input
              readOnly
              autoFocus
              value={idsText}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Список id"
              className="h-8 w-full min-w-0 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg"
            />
            <span className="text-[11.5px] text-fg-faint">буфер недоступен — скопируйте руками</span>
          </div>
        ) : null}
        {error ? (
          <div className="basis-full">
            <ErrorLine error={error} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DeleteButton({ busy, onClick }: { busy: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`${btn} hover:text-err`} disabled={busy} onClick={onClick}>
      <Trash2 size={13} />
      Удалить
    </button>
  );
}

function VerdictCard({
  onVerdict,
  onClose,
  busy,
}: {
  onVerdict: (verdict: Verdict | null) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  usePopoverClose(box, onClose);
  return (
    <div
      ref={box}
      className="absolute bottom-full left-0 z-10 mb-2 grid w-[180px] gap-1 rounded-[var(--radius-card)] border border-border bg-bg p-2 shadow-xl"
      data-annot="вердикт выделенных"
    >
      <button type="button" className={`${btn} text-ok`} disabled={busy} onClick={() => onVerdict("works")}>
        {verdictLabel.works}
      </button>
      <button type="button" className={`${btn} text-err`} disabled={busy} onClick={() => onVerdict("fails")}>
        {verdictLabel.fails}
      </button>
      <button type="button" className={btnGhost} disabled={busy} onClick={() => onVerdict(null)}>
        снять оценку
      </button>
    </div>
  );
}

function LibraryCard({
  count,
  lastLibraryId,
  onMove,
  onCopy,
  onClose,
  busy,
}: {
  count: number;
  lastLibraryId: number | null;
  onMove: (libraryId: number) => Promise<void>;
  onCopy: (libraryId: number) => Promise<void>;
  onClose: () => void;
  busy: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [how, setHow] = useState<"move" | "copy">("move");
  const [error, setError] = useState<unknown>(null);
  usePopoverClose(box, onClose);

  const put = async (libraryId: number) => {
    setError(null);
    try {
      await (how === "move" ? onMove(libraryId) : onCopy(libraryId));
      onClose();
    } catch (exc) {
      setError(exc);
    }
  };

  return (
    <div
      ref={box}
      className="absolute right-0 bottom-full z-10 mb-2 grid w-[320px] gap-2 rounded-[var(--radius-card)] border border-border bg-bg p-3 shadow-xl"
      data-annot="выделенные в другую библиотеку"
    >
      <Segmented<"move" | "copy">
        label="действие"
        value={how}
        onPick={setHow}
        options={[
          {
            value: "move",
            label: "переложить",
            title: "Референс уйдёт из этой библиотеки",
          },
          {
            value: "copy",
            label: "скопировать",
            title: "Останется и здесь, и в выбранной",
          },
        ]}
      />
      <LibraryPicker
        initialLibraryId={lastLibraryId}
        defaultName=""
        busy={busy}
        submitLabel={`${how === "move" ? "Переложить" : "Скопировать"} ${num(count)}`}
        onPick={put}
      />
      <ErrorLine error={error} />
    </div>
  );
}
